import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { COPPER } from "../VestaAuth";
import { B, ago } from "./common";
import { Avatar } from "./PostCard";

const linkify = (t) => t.split(/(https?:\/\/\S+)/g).map((x, i) => (/^https?:\/\//.test(x)
  ? <a key={i} href={x.startsWith(window.location.origin) ? x.slice(window.location.origin.length) : x} target={x.startsWith(window.location.origin) ? undefined : "_blank"} rel="noreferrer" className="underline" data-testid="bacheca-dm-link">{x.includes("/vesta/bacheca?post=") ? "Apri il post" : x}</a> : x));

const who = (o) => (o?.staff ? `${o.name} · Torri` : o?.partner_name || o?.name);

function Thread({ cid, onBack }) {
  const [d, setD] = useState(null);
  const [t, setT] = useState("");
  const end = useRef(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get(`${B}/dm/${cid}/messages`).then((r) => alive && setD(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [cid]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [d?.messages?.length]);
  const send = async () => {
    if (!t.trim()) return;
    const testo = t; setT("");
    try { const r = await api.post(`${B}/dm/${cid}/messages`, { testo }); setD((x) => ({ ...x, messages: [...x.messages, r.data] })); }
    catch (e) { setT(testo); toast.error(e.response?.data?.detail || "Messaggio non inviato"); }
  };
  if (!d) return <Loader2 className="m-4 h-5 w-5 animate-spin" style={{ color: COPPER }} />;
  return (
    <div className="flex h-full flex-col" data-testid="bacheca-dm-thread">
      <button type="button" onClick={onBack} className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-white" data-testid="bacheca-dm-back"><ArrowLeft className="h-4 w-4" />{who(d.conv.other)}</button>
      <div className="flex-1 space-y-2 overflow-y-auto pr-1">
        {d.messages.map((m) => (
          <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.mine ? "rounded-br-sm text-black" : "rounded-bl-sm bg-neutral-800 text-white"}`} style={m.mine ? { background: COPPER } : {}} data-testid="bacheca-dm-msg">
              <div className="whitespace-pre-line break-words">{linkify(m.testo)}</div>
              <div className={`mt-0.5 text-[10px] ${m.mine ? "text-black/60" : "text-neutral-500"}`}>{ago(m.at)}</div>
            </div>
          </div>
        ))}
        {d.messages.length === 0 && <p className="text-xs text-neutral-500">Inizia la conversazione.</p>}
        <div ref={end} />
      </div>
      <div className="mt-3 flex gap-2">
        <input value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Scrivi un messaggio…"
          className="h-10 min-w-0 flex-1 rounded-full border border-neutral-800 bg-neutral-900 px-4 text-sm text-white outline-none focus:border-[#C17F59]" data-testid="bacheca-dm-input" />
        <button type="button" onClick={send} disabled={!t.trim()} className="shrink-0 rounded-full p-2.5 text-black disabled:opacity-40" style={{ background: COPPER }} data-testid="bacheca-dm-send"><Send className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export default function Messages({ open, onOpenChange, cid, setCid }) {
  const [list, setList] = useState(null);
  useEffect(() => { if (open && !cid) api.get(`${B}/dm`).then((r) => setList(r.data)).catch(() => setList([])); }, [open, cid]);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col border-neutral-800 bg-neutral-950 text-white sm:max-w-md" data-testid="bacheca-dm-sheet">
        <SheetHeader><SheetTitle className="font-heading" style={{ color: COPPER }}>Messaggi privati</SheetTitle><SheetDescription className="sr-only">Conversazioni private</SheetDescription></SheetHeader>
        <div className="min-h-0 flex-1 pt-3">
          {cid ? <Thread cid={cid} onBack={() => setCid(null)} /> : !list ? <Loader2 className="h-5 w-5 animate-spin" style={{ color: COPPER }} /> : (
            <div className="space-y-1" data-testid="bacheca-dm-list">
              {list.map((c) => (
                <button key={c.id} type="button" onClick={() => setCid(c.id)} className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-neutral-900" data-testid={`bacheca-dm-conv-${c.id}`}>
                  <Avatar a={c.other} logo={c.other.logo} foto={c.other.foto} size={40} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">{who(c.other)}</div>
                    <div className="truncate text-xs text-neutral-500">{c.last_text || "Nessun messaggio"}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-[10px] text-neutral-500">{c.last_at && ago(c.last_at)}</div>
                    {c.unread > 0 && <span className="mt-1 inline-block rounded-full px-1.5 text-[10px] font-bold text-black" style={{ background: COPPER }}>{c.unread}</span>}
                  </div>
                </button>
              ))}
              {list.length === 0 && <p className="text-sm text-neutral-500">Nessuna conversazione. Apri il profilo di un installatore e tocca «Scrivi in privato».</p>}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
